import * as React from 'react';
import formatNumber from './../../utilities/format-number';
import calculateTotalScore from './../../utilities/calculate-total-score';

const INITIAL_BUFFER = 5000;
export const INITIAL_SCORE = 250;

interface Props {
    bufferMilliseconds: number;
    isRoundInterval: boolean;
    roundCount: number;
    roundIndex: number;
    roundScores: RoundScore[][];
    scoreMilliseconds: number;
    updateScore: (score: number) => void;
}

interface State {
    buffer: number;
    score: number;
}

export default class Timer extends React.Component<Props, State> {

    interval: number;
    startTime: number;

    /**
     * Get the current time.
     * @returns  The current time in milliseconds.
     */
    static getCurrentTime(): number {
        return window.performance ? window.performance.now() : Date.now();
    }

    constructor(props: never) {
        super(props);
        this.props.updateScore(INITIAL_SCORE);
        this.state = this.startTimer();
    }

    /**
     * The timer continually ticks down. However, if the user submits their response, there is a round interval where
     * they see their score and the correct answers. The timer must be paused during this interval. 
     * 
     * After a round interval has completed, the timer should be started again (i.e. at the beginning of a new 
     * round).
     * 
     * @param nextProps  The next state containing a property to say if the player is in a round interval or not.
     */
    componentWillReceiveProps(nextProps: Props): void {
        if (nextProps.isRoundInterval === true) {
            window.cancelAnimationFrame(this.interval);
        } else if (nextProps.roundIndex !== this.props.roundIndex) {
            this.setState(this.startTimer());
        }
    }

    /**
     * Decrement the buffer. This counts down until the user starts losing score. Once the buffer is depleted, start
     * taking points away from the score.
     */
    depleteBuffer(): void {

        const remaining = this.getElapsedTime(this.props.bufferMilliseconds);
        if (remaining >= 1) {
            this.startTime = Timer.getCurrentTime();
            this.setState({ buffer: 0 });
            this.interval = requestAnimationFrame(this.depleteScore.bind(this));
        } else {
            this.setState({ buffer: INITIAL_BUFFER * (1 - remaining) });
            this.interval = requestAnimationFrame(this.depleteBuffer.bind(this));
        }

    }

    /**
     * After the buffer is all used up, the score starts counting down.
     */
    depleteScore(): void {

        const remaining = this.getElapsedTime(this.props.scoreMilliseconds);
        if (remaining <= 1) {
            const score = INITIAL_SCORE * (1 - remaining);
            this.props.updateScore(score);
            this.setState({ score: score > 0 ? Math.round(score) : 0 });
            this.interval = requestAnimationFrame(this.depleteScore.bind(this));
        } else {
            this.props.updateScore(0);
            this.setState({ score: 0 });
        }

    }

    /**
     * Calculate what percentage of the total available time has been used.
     * @returns  Percentage of elapsed milliseconds.
     */
    getElapsedTime(totalTime: number): number {
        return (Timer.getCurrentTime() - this.startTime) / totalTime;
    }

    /**
     * Render a score with two loading bars either side. The loading bars decrease until the score/time starts 
     * ticking down.
     */
    render(): JSX.Element {

        const bufferRemaining = this.state.buffer / INITIAL_BUFFER;
        const bufferStyle = { width: `${bufferRemaining * 100}%` };

        // Add up all round scores.
        const totalScore = formatNumber(calculateTotalScore(this.props.roundScores));

        return (
            <div className="timer">
                <div className="timer-round">
                    Round {this.props.roundIndex + 1} of {this.props.roundCount}
                </div>
                <div className="timer-score">
                    {totalScore}
                    <div className="timer-buffer">
                        <div style={bufferStyle}>&nbsp;</div>
                    </div>
                </div>
                <div className="timer-remaining">
                    {formatNumber(this.state.score)}
                    <div className="timer-buffer">
                        <div style={bufferStyle}>&nbsp;</div>
                    </div>
                </div>
            </div>
        );

    }

    /**
     * Set or reset the round score and then start/restart the timer.
     */
    startTimer(): State {
        this.startTime = Timer.getCurrentTime();
        this.interval = requestAnimationFrame(this.depleteBuffer.bind(this));
        this.props.updateScore(INITIAL_SCORE);
        return { score: INITIAL_SCORE, buffer: INITIAL_BUFFER };
    }

}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/globals/components/timer/timer.tsx